import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Container,
    Typography,
    Divider,
    TextField,
    Autocomplete,
    Chip,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
} from "@mui/material";

import queryGraphql from "../../../helpers/ApiInterface";
import { ExpandMore } from "@mui/icons-material";
import MainDrawer from "../../MainDrawer";
import DataZonesRequestsTable from "./DataZonesRequestsTable";
import MiniBot from "../../MiniBot";

interface SearchOptions {
    chipLabel: string;
    dropdownLabel: string;
    category: string;
    groupBy: string;
}

let searchOptions: SearchOptions[] = [];

const DataZonesRequestsPage: React.FC = () => {
    const [dataZones, setDataZones] = useState<any[]>([]);
    const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
    const [updates, setUpdates] =  useState<any[]>([]);
    const navigate = useNavigate();

    React.useEffect(() =>{
        const fetchData = async () => {
            try {
                const data = await queryGraphql(`
                        query Query {
                        resourceStatuses {
                            id
                            currentStatus
                            resourceId
                        }
                        }
                    `);
                if(data.resourceStatuses){
                    setUpdates(data.resourceStatuses);
                }
                }
                catch(e){
            console.log(e);
                }
    }
    fetchData();
    },[]);  
    
    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await queryGraphql(`
                    query GetDataplexZones {
                      getDataplexZones {
                        id
                        name
                        solutionType
                        cloudProvider
                        status
                        details {
                          dataplexZone {
                            projectId
                            location
                            type
                          }
                        }
                        reasonForRejection
                        createdAt
                        updatedAt
                        solutionOwner {
                          id
                          name
                          email
                          googleProfilePicture
                        }
                        solutionApprover {
                          id
                          name
                          email
                          googleProfilePicture
                        }
                        parentSolution {
                            id
                            name
                        }
                      }
                    }
                `);

                const rawDataZonesData = data.data?.getDataplexZones || data.getDataplexZones || [];

                let dataZonesArr = rawDataZonesData.map((e: any) => {
                    return {
                        id: e.id,
                        zoneName: e.name,
                        lakeName: e.parentSolution?.name,
                        status: e.status ? e.status.toLowerCase() : 'pending',
                        createdAt: e.createdAt ? new Date(Number(e.createdAt)).toISOString() : null,
                        actionedAt: e.updatedAt ? new Date(Number(e.updatedAt)).toISOString() : null,
                        
                        zoneOwnerName: e.solutionOwner?.name,
                        zoneOwnerAvatar: e.solutionOwner?.googleProfilePicture,
                        
                        zoneApproverName: e.solutionApprover?.name,
                        zoneApproverAvatar: e.solutionApprover?.googleProfilePicture,

                        zoneType: e.details?.dataplexZone?.type,
                        cloudProvider: e.cloudProvider,
                        projectId: e.details?.dataplexZone?.projectId,

                        reasonForRejection: e.reasonForRejection,
                    };
                });
                setDataZones(dataZonesArr);

                searchOptions = dataZonesArr
                    .map((e: any) => {
                        const options = [
                            {
                                category: "zoneName",
                                groupBy: "DATA ZONE NAME",
                                chipLabel: `Zone Name: ${e.zoneName}`,
                                dropdownLabel: e.zoneName,
                            },
                            {
                                category: "lakeName",
                                groupBy: "LAKE NAME",
                                chipLabel: `Lake Name: ${e.lakeName}`,
                                dropdownLabel: e.lakeName,
                            },
                            {
                                category: "zoneOwnerName",
                                groupBy: "OWNER",
                                chipLabel: `Owner: ${e.zoneOwnerName}`,
                                dropdownLabel: e.zoneOwnerName,
                            },
                            {
                                category: "zoneApproverName",
                                groupBy: "APPROVER",
                                chipLabel: `Approver: ${e.zoneApproverName}`,
                                dropdownLabel: e.zoneApproverName,
                            },
                        ];
                        
                        if (e.zoneType) {
                            options.push({
                               category: "zoneType",
                               groupBy: "ZONE TYPE",
                               chipLabel: `Type: ${e.zoneType}`,
                               dropdownLabel: e.zoneType,
                           });
                       }
                       if (e.cloudProvider) {
                        options.push({
                           category: "cloudProvider",
                           groupBy: "CLOUD PROVIDER",
                           chipLabel: `Provider: ${e.cloudProvider}`,
                           dropdownLabel: e.cloudProvider,
                       });
                   }
                        if (e.status) {
                            options.push({
                                category: "status",
                                groupBy: "STATUS",
                                chipLabel: `Status: ${e.status.charAt(0).toUpperCase() + e.status.slice(1)}`,
                                dropdownLabel: e.status.charAt(0).toUpperCase() + e.status.slice(1),
                            });
                        }
                        return options;
                    })
                    .flat();

                searchOptions = searchOptions.filter(
                    (obj, index, self) =>
                        obj.dropdownLabel &&
                        index ===
                        self.findIndex(
                            (t) => t.category === obj.category && t.dropdownLabel === obj.dropdownLabel
                        )
                );
                searchOptions.sort((a, b) => {
                    if (a.groupBy > b.groupBy) return 1;
                    if (a.groupBy < b.groupBy) return -1;
                    if (a.dropdownLabel > b.dropdownLabel) return 1;
                    if (a.dropdownLabel < b.dropdownLabel) return -1;
                    return 0;
                });

            } catch (error) {
                console.log(error);
            }
        };

        fetchData();
    }, []);

    const filteredDataZones =
        searchQuery.length === 0
            ? dataZones
            : dataZones.filter(
                    (dataZone: any) =>
                        (searchQuery.filter((e) => e.category === "zoneName").length > 0
                            ? searchQuery.filter((e) => e.category === "zoneName").map((e) => e.dropdownLabel?.toLowerCase()).includes(dataZone.zoneName?.toLowerCase())
                            : true) &&
                        (searchQuery.filter((e) => e.category === "lakeName").length > 0
                            ? searchQuery.filter((e) => e.category === "lakeName").map((e) => e.dropdownLabel?.toLowerCase()).includes(dataZone.lakeName?.toLowerCase())
                            : true) &&
                        (searchQuery.filter((e) => e.category === "zoneOwnerName").length > 0
                            ? searchQuery.filter((e) => e.category === "zoneOwnerName").map((e) => e.dropdownLabel?.toLowerCase()).includes(dataZone.zoneOwnerName?.toLowerCase())
                            : true) &&
                         (searchQuery.filter((e) => e.category === "zoneApproverName").length > 0
                            ? searchQuery.filter((e) => e.category === "zoneApproverName").map((e) => e.dropdownLabel?.toLowerCase()).includes(dataZone.zoneApproverName?.toLowerCase())
                            : true) &&
                        (searchQuery.filter((e) => e.category === "zoneType").length > 0
                            ? searchQuery.filter((e) => e.category === "zoneType").map((e) => e.dropdownLabel?.toLowerCase()).includes(dataZone.zoneType?.toLowerCase())
                            : true) &&
                        (searchQuery.filter((e) => e.category === "cloudProvider").length > 0
                            ? searchQuery.filter((e) => e.category === "cloudProvider").map((e) => e.dropdownLabel?.toLowerCase()).includes(dataZone.cloudProvider?.toLowerCase())
                            : true) &&
                        (searchQuery.filter((e) => e.category === "status").length > 0
                            ? searchQuery.filter((e) => e.category === "status").map((e) => e.dropdownLabel?.toLowerCase()).includes(dataZone.status?.toLowerCase())
                            : true)
              );
    
    const filteredActiveDataZones = filteredDataZones.filter(
        (p: any) => p.status === "active" || p.status === "approved"
    );
    const filteredPendingDataZones = filteredDataZones.filter(
        (p: any) => p.status === "pending"
    );
    const filteredRejectedDataZones = filteredDataZones.filter(
        (p: any) => p.status === "rejected"
    );

    return (
        <Box sx={{ display: "flex" }}>
            <MainDrawer index={"requests-data-zones"} />
            <MiniBot/>
            <Box
                component="main"
                sx={{
                    flex: 1,
                    bgcolor: "#fafafa",
                    minHeight: window.innerHeight,
                }}
            >
                <Container maxWidth={false} disableGutters>
                    <Box bgcolor="#FFFFFF" p={2} mb="18px">
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginY: 1,
                                marginLeft: 2,
                            }}
                        >
                            <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold">
                                Requests
                            </Typography>
                            <IconButton
                                aria-label="Help" 
                                onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
                                sx={{ padding: 0, border: 'none', mr:2, '&:hover': { backgroundColor: 'action.hover' } }}
                              >
                                <Box component="img" src="/assets/Help.svg" alt="" sx={{ width: 40,  height: 40, display: 'block' }} />
                              </IconButton>
                        </Box>
                        <Divider sx={{ marginY: 1 }} />
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mx: 2 }}>
                            <Box>
                                <Typography fontFamily="Gilroy-SemiBold" fontSize="16px">
                                    {`DATA ZONES (${dataZones.length})`}
                                </Typography>
                                <Typography fontFamily="Gilroy-Regular" fontSize="14px">
                                    {`View all the requests raised for Data Zones`}
                                </Typography>
                            </Box>
                            <Autocomplete
                                multiple
                                options={searchOptions}
                                filterSelectedOptions
                                getOptionLabel={(option) => option.dropdownLabel}
                                defaultValue={[]}
                                groupBy={(option) => option.groupBy}
                                renderTags={(tagValue, getTagProps) => {
                                    return tagValue.map((option, index) => (
                                        <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} />
                                    ));
                                }}
                                onChange={(e, newVal, reason) => {
                                    setSearchQuery(newVal);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="filled"
                                        placeholder="Search by zone name, owner, status etc"
                                        fullWidth
                                        size="small"
                                        hiddenLabel
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <img src="/assets/search-normal.svg" alt="Filter Icon" style={{ width: "16px", paddingLeft: "4px" }}/>
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            disableUnderline: true,
                                            style: { backgroundColor: "#f4f4f4", minHeight: "45px", width: "500px", borderRadius: "8px", fontFamily: "Gilroy-Regular", fontSize: "14px" },
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    </Box>
                    <Box m={2}>
                        <Accordion
                            defaultExpanded={filteredPendingDataZones.length > 0 ? true : false}
                            elevation={0}
                            sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={filteredPendingDataZones.length === 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/clock.svg" alt="Pending Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Pending Data Zones ({filteredPendingDataZones.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {filteredPendingDataZones.length > 0 && (
                                    <DataZonesRequestsTable dataZones={filteredPendingDataZones} resourceStatuses={updates}/>
                                )}
                            </AccordionDetails>
                        </Accordion>
                        <Accordion
                            defaultExpanded={true}
                            elevation={0}
                            sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={filteredActiveDataZones.length === 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/tickcircle.svg" alt="Active Icon" style={{ width: "20px", marginRight: "8px" }}/>
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Active Data Zones ({filteredActiveDataZones.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {filteredActiveDataZones.length > 0 && (
                                    <DataZonesRequestsTable dataZones={filteredActiveDataZones} resourceStatuses={updates}/>
                                )}
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            defaultExpanded={filteredRejectedDataZones.length > 0 ? true : false}
                            elevation={0}
                            sx={{ borderRadius: "8px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={filteredRejectedDataZones.length === 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/reject.svg" alt="Rejected Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Rejected Data Zones ({filteredRejectedDataZones.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {filteredRejectedDataZones.length > 0 && (
                                    <DataZonesRequestsTable dataZones={filteredRejectedDataZones} resourceStatuses={updates}/>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default DataZonesRequestsPage;